import {useCallback, useMemo} from "react";
import {Trans, useTranslation} from "react-i18next";
import {useRouteMatch} from "react-router-dom";

import {IMonthCityStats} from "../../../../../api/src/db_queries/month_city_stats_query";
import {IMonthCityTypeStats} from "../../../../../api/src/db_queries/month_city_type_stats_query";
import {getCityMapRegionParams, getCityOption} from "../../../../../config/cities";
import {mapCitySlugToRegionType} from "../../../../../config/regions";
import {originDate} from "../../../../../utils/dates";
import {useAppDispatch, useAppSelector} from "../../../../../utils/hooks/store_hooks";
import {mapPathnameToOfferType, OfferType} from "../../../common/app/routing/offer_type";
import {BarChartCard} from "../../../components/charts/chart_cards/BarChartCard";
import {ColumnStackedChartCard} from "../../../components/charts/chart_cards/ColumnStackedChartCard";
import {LineColumnChartCard} from "../../../components/charts/chart_cards/LineColumnChartCard";
import {MultilineChartCard} from "../../../components/charts/chart_cards/MultilineChartCard";
import {Grid} from "../../../components/Grid";
import {withDateGuard} from "../../../components/hoc/withDateGuard";
import {withGTM} from "../../../components/hoc/withGTM";
import {ChartsLayout as Layout} from "../../../components/layout/charts_layout/ChartsLayout";
import {
    clearChartsRegionPoc,
    selectChartsSelectedRegion,
    setChartsSelectedRegion
} from "../../../components/map/redux/map_city_poc_slice";
import {IOsmDistrictPolygon} from "../../../components/map/types/osm_district_polygon";
import {
    buildDistrictDrillDownSelection,
    buildMapParentRegionSnapshot,
    restoreParentRegionSelection
} from "../../../components/map/utils/district_map_drill_down";
import {toCityOutlineOsmMapParams} from "../../../components/map/utils/osm_map_region_params";
import {Division, MathEquation} from "../../../components/MathEquation";
import {selectAuthStatus} from "../../auth/auth_slice";
import {selectLatestEntry} from "../../ssr/redux/latest_entry_slice";
import {pageviewAlgolyticsHit} from "../../tracking/algolytics/pageview_hits";
import {usePageview} from "../../tracking/use_pageview";
import {ViewType} from "../../tracking/view_type";
import {
    useGetChartsMonthCityStatsQuery,
    useGetChartsMonthCityTypeStatsQuery,
    useGetChartsMonthDistrictStatsQuery
} from "../api/charts_platform_api";
import {DistrictOsmMapCard} from "../components/DistrictOsmMapCard";
import {useChartsRegionId} from "../hooks/use_charts_region_id";
import {useEffectiveCitySlug} from "../hooks/use_effective_city_slug";
import {useSelectedRegionDisplay} from "../hooks/use_selected_region_display";
import {buildDistrictMapValuesBySlug, resolveMapMetric} from "../utils/build_district_map_values_by_slug";
import {mapRtkQueryStatusToRequestStatus} from "../utils/rtq_status_to_request_status";

export const Demand = withDateGuard(
    withGTM(() => {
        const {path} = useRouteMatch();
        const citySlug = useEffectiveCitySlug();
        const {isKnownCity, regionLabel} = useSelectedRegionDisplay();
        const regionType = mapCitySlugToRegionType(citySlug);
        usePageview(() => {
            pageviewAlgolyticsHit({citySlug, regionType, viewType: ViewType.DEMAND_DETAILS});
        });
        const checkOfferType = mapPathnameToOfferType(path);
        const isFlatOfferType = checkOfferType === OfferType.FLAT;
        const {t, i18n} = useTranslation();
        const {currentDate} = useAppSelector(selectLatestEntry);
        const lastMonth = currentDate ?? "";
        const {isLoggedIn, isAuthorized} = useAppSelector(selectAuthStatus);
        const shouldFetch = isLoggedIn && isAuthorized;
        const dispatch = useAppDispatch();
        const chartsSelectedRegion = useAppSelector(selectChartsSelectedRegion);
        const {regionId, districtChartsRegionId, effectiveCitySlug, hasChartsAccess, isRegionIdReady} =
            useChartsRegionId();
        const isPermitted = isAuthorized && hasChartsAccess;
        const chartsCitySlug = effectiveCitySlug ?? citySlug;
        const isAgglomerationView = getCityOption(chartsCitySlug).useAgglomerationApi;
        const routeMapParams = useMemo(
            () => getCityMapRegionParams(chartsCitySlug, regionId ?? undefined),
            [chartsCitySlug, regionId]
        );
        const baseOsmMapParams = useMemo(
            () =>
                chartsSelectedRegion != null
                    ? {
                          regionsMapSlug: chartsSelectedRegion.regionsMapSlug,
                          regionsMapParentId: chartsSelectedRegion.regionsMapParentId,
                          regionsMapRegionId: chartsSelectedRegion.regionsMapRegionId,
                          regionsMapChildType: chartsSelectedRegion.regionsMapChildType
                      }
                    : routeMapParams,
            [chartsSelectedRegion, routeMapParams]
        );
        const hasDistrictStatsMap = baseOsmMapParams.regionsMapParentId != null;
        const skipChartsFetch = !shouldFetch || !isRegionIdReady || regionId == null;

        const {
            data = [],
            isError,
            isSuccess
        } = useGetChartsMonthCityStatsQuery(
            {
                region_id: regionId!,
                date_start: originDate,
                date_end: lastMonth,
                scenario: "demand"
            },
            {skip: skipChartsFetch}
        );
        const {
            data: typeData = [],
            isError: isTypeDataError,
            isSuccess: isTypeDataSuccess
        } = useGetChartsMonthCityTypeStatsQuery(
            {
                region_id: regionId!,
                date_start: originDate,
                date_end: lastMonth,
                offer_type: "Mieszkania",
                scenario: "demand"
            },
            {skip: skipChartsFetch}
        );

        const {data: monthDistrictData = []} = useGetChartsMonthDistrictStatsQuery(
            {
                region_id: districtChartsRegionId!,
                date_start: lastMonth,
                date_end: lastMonth,
                scenario: "demand",
                ...(isFlatOfferType ? {offer_type: "Mieszkania"} : {})
            },
            {
                skip: skipChartsFetch || districtChartsRegionId == null || (isAgglomerationView && !hasDistrictStatsMap)
            }
        );

        const isCityMultiDistrictView = baseOsmMapParams.regionsMapParentId != null;
        const showDistrictPolygons = isCityMultiDistrictView && monthDistrictData.length > 0;
        const osmMapParams = useMemo(
            () => (showDistrictPolygons ? baseOsmMapParams : toCityOutlineOsmMapParams(baseOsmMapParams)),
            [baseOsmMapParams, showDistrictPolygons]
        );

        const monthStatsData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
        const monthTypeStatsData = useMemo(() => (Array.isArray(typeData) ? typeData : []), [typeData]);
        const dataStatus = mapRtkQueryStatusToRequestStatus(isError, isSuccess);
        const typeDataStatus = mapRtkQueryStatusToRequestStatus(isTypeDataError, isTypeDataSuccess);
        const monthData = monthStatsData.length > 0 ? [monthStatsData[monthStatsData.length - 1]] : [];
        const flatMonthData = monthTypeStatsData.length > 0 ? [monthTypeStatsData[monthTypeStatsData.length - 1]] : [];

        const districtsMapAvailable = Boolean(
            osmMapParams.regionsMapSlug ||
                osmMapParams.regionsMapParentId != null ||
                osmMapParams.regionsMapRegionId != null
        );
        const toDemandPercent = (demand: unknown) => {
            const value = resolveMapMetric(demand);

            return value == null ? undefined : value * 100;
        };
        const lastMapStats = isFlatOfferType ? monthTypeStatsData.at(-1) : monthStatsData.at(-1);
        const districtDemandBySlug = useMemo(
            () =>
                buildDistrictMapValuesBySlug({
                    districtsMapAvailable,
                    monthDistrictData,
                    chartsCitySlug,
                    districtMetric: (d) => toDemandPercent(d.demand),
                    cityLevelMetric: toDemandPercent(lastMapStats?.demand),
                    cityLevelStatsRecord: lastMapStats,
                    extraSlugKeys: [osmMapParams.regionsMapSlug, chartsSelectedRegion?.slug]
                }),
            [
                chartsCitySlug,
                chartsSelectedRegion?.slug,
                districtsMapAvailable,
                lastMapStats,
                monthDistrictData,
                osmMapParams.regionsMapSlug
            ]
        );
        const districtDrillDownEnabled = showDistrictPolygons && monthDistrictData.length > 0;
        const districtStatsCitySlug = getCityOption(chartsCitySlug).requestCitySlug;
        const mapParentRegion = chartsSelectedRegion?.parentRegion;

        const handleDistrictPolygonClick = useCallback(
            (district: IOsmDistrictPolygon) => {
                if (
                    districtChartsRegionId == null ||
                    baseOsmMapParams.regionsMapParentId == null ||
                    chartsCitySlug == null
                ) {
                    return;
                }

                const parentLabel = chartsSelectedRegion?.label ?? t(`city.${citySlug}.nominative`);
                const parentSlug = chartsSelectedRegion?.slug ?? baseOsmMapParams.regionsMapSlug ?? "";

                dispatch(
                    setChartsSelectedRegion(
                        buildDistrictDrillDownSelection(
                            district,
                            chartsCitySlug,
                            buildMapParentRegionSnapshot({
                                isRouteCity: chartsSelectedRegion == null,
                                regionId: districtChartsRegionId,
                                slug: parentSlug,
                                label: parentLabel,
                                citySlug: chartsSelectedRegion?.citySlug ?? chartsCitySlug,
                                hasChartsAccess,
                                mapParams: baseOsmMapParams
                            })
                        )
                    )
                );

                window.userengage?.("event.[BD]_district_click", {
                    city: chartsCitySlug,
                    district: district.slug_district ?? district.slug
                });
            },
            [baseOsmMapParams, chartsCitySlug, chartsSelectedRegion, citySlug, dispatch, districtChartsRegionId, t]
        );

        const handleBackToMapParent = useCallback(() => {
            const parent = chartsSelectedRegion?.parentRegion;
            if (!parent) {
                return;
            }

            if (parent.isRouteCity) {
                dispatch(clearChartsRegionPoc());
            } else {
                dispatch(setChartsSelectedRegion(restoreParentRegionSelection(parent)));
            }
        }, [chartsSelectedRegion, dispatch]);

        return (
            <Layout>
                <Grid type={districtsMapAvailable ? "subpage" : "subpageMapless"}>
                    <LineColumnChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart1"
                        chartId="demand-chart1"
                        title={<Trans>{t("demand.chart1.title")}</Trans>}
                        helper={
                            <>
                                {t("demand.chart1.description")}
                                <br />
                                <br />
                                <MathEquation
                                    leftSide={t("common.formulas.number_of_offers")}
                                    rightSide={`${t("common.formulas.sold_offers")} + ${t(
                                        "common.formulas.demand_offers"
                                    )}`}
                                />
                                <br />
                                <MathEquation
                                    leftSide={t("common.formulas.demand_indicator")}
                                    rightSide={
                                        <Division
                                            dividend={t("common.formulas.sold_offers")}
                                            divisor={t("common.formulas.number_of_offers")}
                                        />
                                    }
                                />
                                <br />
                                <Trans>{t("demand.chart1.additional_description")}</Trans>
                            </>
                        }
                        data={isFlatOfferType ? typeData : data}
                        dataStatus={isFlatOfferType ? typeDataStatus : dataStatus}
                        columnFields={["available_offers", "sold"]}
                        lineField="demand"
                        lineAxisMinValue={0}
                        tooltipUnits={{
                            line: "%"
                        }}
                        lineValueMultiplier={100}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <MultilineChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart2"
                        chartId="demand-chart2"
                        title={<Trans>{t("demand.chart2.title")}</Trans>}
                        helper={
                            <>
                                {t("demand.chart2.description")}
                                <br />
                                <br />
                                <MathEquation
                                    leftSide={t("common.formulas.demand_distribution")}
                                    rightSide={
                                        <Division
                                            dividend={t("common.formulas.rooms_seekers")}
                                            divisor={t("common.formulas.all_seekers")}
                                        />
                                    }
                                />
                            </>
                        }
                        data={data}
                        dataStatus={dataStatus}
                        fields={[
                            "demand_studio",
                            "demand_2_rooms",
                            "demand_3_rooms",
                            "demand_4_plus_rooms",
                            ...(isFlatOfferType ? [] : ["demand_house"])
                        ]}
                        unit="%"
                        valueMultiplier={100}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <BarChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart3"
                        chartId="demand-chart3"
                        title={<Trans>{t("demand.chart3.title")}</Trans>}
                        helper={
                            <>
                                <Trans>{t("demand.chart3.description")}</Trans>
                                <br />
                                <br />
                                <MathEquation
                                    leftSide={t("common.formulas.demand_distribution")}
                                    rightSide={
                                        <Division
                                            dividend={
                                                <Trans>{t("common.formulas.seekers_within_pricing_segment")}</Trans>
                                            }
                                            divisor={t("common.formulas.all_seekers")}
                                        />
                                    }
                                />
                            </>
                        }
                        data={isFlatOfferType ? flatMonthData : monthData}
                        dataStatus={isFlatOfferType ? typeDataStatus : dataStatus}
                        fields={[
                            "demand_offers_to_6k",
                            "demand_offers_between_6k_and_7k",
                            "demand_offers_between_7k_and_8k",
                            "demand_offers_between_8k_and_9k",
                            "demand_offers_between_9k_and_10k",
                            "demand_offers_between_10k_and_12k",
                            "demand_offers_between_12k_and_15k",
                            "demand_offers_between_15k_and_20k",
                            "demand_offers_above_20k"
                        ]}
                        height={250}
                        unit="%"
                        valueMultiplier={100}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <MultilineChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart4"
                        chartId="demand-chart4"
                        title={<Trans>{t("demand.chart4.title")}</Trans>}
                        helper={
                            <>
                                <Trans>{t("demand.chart4.description")}</Trans>
                                <br />
                                <br />
                                <MathEquation
                                    leftSide={t("common.formulas.demand_distribution")}
                                    rightSide={
                                        <Division
                                            dividend={
                                                <Trans>{t("common.formulas.seekers_within_pricing_segment")}</Trans>
                                            }
                                            divisor={t("common.formulas.all_seekers")}
                                        />
                                    }
                                />
                            </>
                        }
                        data={isFlatOfferType ? typeData : data}
                        dataStatus={isFlatOfferType ? typeDataStatus : dataStatus}
                        fields={[
                            "demand_offers_to_300k",
                            "demand_offers_between_300k_and_400k",
                            "demand_offers_between_400k_and_500k",
                            "demand_offers_between_500k_and_700k",
                            "demand_offers_between_700k_and_1_mln",
                            "demand_offers_above_1_mln"
                        ]}
                        unit="%"
                        valueMultiplier={100}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <ColumnStackedChartCard
                        area="chart5"
                        chartId="demand-chart5"
                        title={<Trans>{t("demand.chart5.title")}</Trans>}
                        helper={
                            <>
                                <Trans>{t("demand.chart5.description")}</Trans>
                                <br />
                                <br />
                                <MathEquation
                                    leftSide={t("common.formulas.demand_distribution")}
                                    rightSide={
                                        <Division
                                            dividend={t("common.formulas.seekers_within_investment_size")}
                                            divisor={t("common.formulas.all_seekers")}
                                        />
                                    }
                                />
                            </>
                        }
                        data={monthData}
                        dataStatus={dataStatus}
                        categories={[
                            "investment_type_intimate",
                            "investment_type_small",
                            "investment_type_medium",
                            "investment_type_large",
                            "investment_type_x_large",
                            ...(isFlatOfferType ? [] : ["investment_type_house"])
                        ]}
                        fieldGroups={[
                            [
                                "demand_investment_type_intimate_offer_studio",
                                "demand_investment_type_small_offer_studio",
                                "demand_investment_type_medium_offer_studio",
                                "demand_investment_type_large_offer_studio",
                                "demand_investment_type_x_large_offer_studio",
                                ...(isFlatOfferType ? [] : ["demand_houses_to_100m2"])
                            ],
                            [
                                "demand_investment_type_intimate_offer_2_rooms",
                                "demand_investment_type_small_offer_2_rooms",
                                "demand_investment_type_medium_offer_2_rooms",
                                "demand_investment_type_large_offer_2_rooms",
                                "demand_investment_type_x_large_offer_2_rooms",
                                ...(isFlatOfferType ? [] : ["demand_houses_between_100_and_150m2"])
                            ],
                            [
                                "demand_investment_type_intimate_offer_3_rooms",
                                "demand_investment_type_small_offer_3_rooms",
                                "demand_investment_type_medium_offer_3_rooms",
                                "demand_investment_type_large_offer_3_rooms",
                                "demand_investment_type_x_large_offer_3_rooms",
                                ...(isFlatOfferType ? [] : ["demand_houses_between_150_and_200m2"])
                            ],
                            [
                                "demand_investment_type_intimate_offer_4_plus_rooms",
                                "demand_investment_type_small_offer_4_plus_rooms",
                                "demand_investment_type_medium_offer_4_plus_rooms",
                                "demand_investment_type_large_offer_4_plus_rooms",
                                "demand_investment_type_x_large_offer_4_plus_rooms",
                                ...(isFlatOfferType ? [] : ["demand_houses_between_200_and_250m2"])
                            ],
                            ["", "", "", "", "", ...(isFlatOfferType ? [] : ["demand_houses_above_250m2"])]
                        ]}
                        minValue={0}
                        unit="%"
                        valueMultiplier={100}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    <DistrictOsmMapCard
                        chartId="demand-map"
                        title={
                            <Trans>
                                {isKnownCity
                                    ? t("demand.map.title", {city: chartsCitySlug})
                                    : t("demand.map.title_region", {region: regionLabel})}
                            </Trans>
                        }
                        helper={t("demand.map.description")}
                        visible={districtsMapAvailable}
                        sticky
                        citySlug={chartsCitySlug}
                        regionId={regionId}
                        regionsMapSlug={osmMapParams.regionsMapSlug}
                        regionsMapParentId={osmMapParams.regionsMapParentId}
                        regionsMapChildType={osmMapParams.regionsMapChildType}
                        regionsMapRegionId={osmMapParams.regionsMapRegionId}
                        districtsMapAvailable={districtsMapAvailable}
                        districtValuesBySlug={districtDemandBySlug}
                        districtStatsCitySlug={districtStatsCitySlug}
                        legendType="quantitative"
                        legendColors={["#FFEED4", "#FFE298", "#FFC56D", "#FFA740", "#FF8800"]}
                        metricLabel={t("fields.demand", {defaultValue: "Popyt (%)"})}
                        locale={i18n.language}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                        cityOutlineMetric={!showDistrictPolygons}
                        districtDrillDownEnabled={districtDrillDownEnabled}
                        onDistrictPolygonClick={handleDistrictPolygonClick}
                        onBackToParentRegion={mapParentRegion ? handleBackToMapParent : undefined}
                        backToParentLabel={mapParentRegion ? `← ${mapParentRegion.label}` : undefined}
                    />
                </Grid>
            </Layout>
        );
    })
);
